/**
 * Does this page get a Studio tab, and is the Studio switched on? — STUDIO_DESIGN.md §6.1.
 *
 * TWO INDEPENDENT QUESTIONS, kept apart because they fail differently. The PAGE question is a
 * fact about the route and is known during static export, so the tab strip renders identically
 * on the server and on the client and costs no layout shift. The FLAG question is a fact about
 * the fleet, arrives later, and can only ever TAKE the tab away.
 *
 * WHY NOT `pageData.fromType`. §6.1 specifies "`fromType` contains `image`". Measured over
 * `ssr-data/build/*.json` on 2026-09-17 at 324d1f4f: 78 pages have `toType === 'video'` and
 * exactly ONE of them — `convert-image-to-video` — has `fromType: ['image']`. On the other 77 the
 * field holds the raw extension (`['jpg']`, `['png']`, `['mp4']`, `['mkv']`…), which is not even a
 * member of the `FromType` union (types/types.ts:6-18). So the design's rule lights 1 of the 37
 * image→video pages, and the obvious repair — "no entry in {video, audio, av}" — lights 74,
 * including `mp4-converter`.
 *
 * WHAT IS USED INSTEAD. `toType === 'video'` plus "every accepted extension is an image
 * extension". Measured with the list below: exactly 37 pages — `convert-image-to-video` and the
 * 36 `convert-<ext>-to-video` pages — and zero video-input pages. The list is
 * `convert-image-to-video`'s OWN accept list, and `studio-gate.spec.ts` reads
 * `public/feature-metadata/convert-image-to-video.json` and fails when the two disagree, so a
 * new input format reaches the Studio by regenerating xconfig rather than by editing this file
 * and hoping someone remembers.
 *
 * REVISED 2026-09-18 (owner ruling, seen on QA): `toType === 'video'` alone under-lights the
 * family. `convert-jpg-to-mp4` has the SAME accept list as `convert-jpg-to-video` but its
 * `toType` is the string `'mp4'`, so the tab was missing there — `PageData` has no separate
 * `toExtension` field, `toType` doubles as it. The gate was widened to `toType === 'video'`
 * **or** `toType` is one of three hardcoded extensions (`'mp4' | 'mov' | 'webm'`), still **and**
 * every accepted extension is an image extension.
 *
 * REVISED AGAIN 2026-09-18 (owner ruling, HEAD ≥ dc207a05): the three-extension hardcode was
 * still wrong — the rule is "ANY non-animated-image → video route", not just mp4/mov/webm.
 * `convert-jpg-to-avi`, `convert-png-to-mkv` and 31 other `convert-<img>-to-<container>` families
 * have the exact same shape and were still dark. The fix has two parts:
 *
 * (1) **What counts as a video OUTPUT**, replacing the 3-item hardcode with
 * `VIDEO_CONTAINER_EXTENSIONS` — every `toExtension` xconfig itself calls video. Derived from
 * `public/feature-metadata/*.json` (the artefact xconfig's `feature-metadata-generator.ts`
 * writes and `copy:feature-metadata` copies here verbatim): every distinct `toExtension` across
 * all 5,826 files where `toMediaType === 'video'`, excluding the bucket literal `'video'` itself
 * (kept as its own OR branch below, matching the pre-existing code shape) and excluding the 3
 * hub pages (`video-converter`, `video-compressor`, `video-trimmer`) whose `toExtension` is
 * `null` there. Cross-checked 2026-09-18 against xconfig's OWN source —
 * `xconfig/src/definitions/media-formats/video.media-formats.json`'s `transcoders` list (the
 * program `"ff"` entry `MediaFormats` in `xconfig/src/domain/generators/media-formats.ts` reads
 * to derive `encodableVideoExtensions`, which is what `feature-metadata-generator.ts` stamps as
 * `toMediaType: "video"`) — the two lists match exactly, 32 extensions.
 *
 * (2) **The animated-only exclusion.** Widening (1) alone also lights `convert-gif-to-mp4` and
 * `convert-apng-to-webm`: `gif` and `apng` are themselves members of `STUDIO_IMAGE_EXTENSIONS`
 * (GIF/APNG can carry a single still frame), so a page whose ENTIRE accept list is `['gif']` or
 * `['apng']` still passes "every accepted extension is an image extension". The owner ruled these
 * routes dark — they convert an animated image, not a still, to video. The obvious encoding of
 * that — "the accepted set is a subset of `{gif, apng}`" — does NOT work: measured 2026-09-18,
 * `convert-apng-to-<container>`'s OWN accept list is `['apng', 'png']` (feature-metadata: APNG's
 * still-frame fallback is a plain PNG upload), which is not a subset of `{gif, apng}` and would
 * stay lit under that rule despite the owner naming it explicitly as an example of a route that
 * must go dark. What actually distinguishes it is the route's own declared identity —
 * `pageData.fromType`, a single-element array carrying the RAW SOURCE extension on every
 * per-extension page (`['gif']`, `['apng']`, `['jpg']`, …; `['image']` only on the broad bucket
 * `convert-image-to-video`) — the same field the very first KDoc paragraph above rejects for a
 * DIFFERENT question ("does the source contain `image`"). Here the question is narrower ("is the
 * declared source itself one specific animated format") and `fromType` answers it correctly where
 * `acceptedExtensions` cannot: `ANIMATED_ONLY_SOURCE_EXTENSIONS` excludes a page whose `fromType`
 * is exactly `['gif']` or `['apng']`, regardless of what else its accept list additionally allows.
 *
 * Measured over `ssr-data/build/*.json` 2026-09-18: 33 video-container suffixes (32 concrete
 * extensions + the `'video'` bucket), each lighting exactly 35 pages (37 image-source pages per
 * suffix from the old rule, minus the 2 the animated-only exclusion removes: `convert-gif-to-*`
 * and `convert-apng-to-*`) — **1,155 lit pages in total**, still zero video-input pages.
 * `studio-gate.spec.ts`'s census pins the per-suffix and total counts.
 *
 * This module imports nothing at all — not even a type. It is reached from
 * `converter-page-base`, which is in the static entry graph, and a service edge there is the
 * crash `file-convert.component.tsx:813-822` records.
 */
import { IMAGE_EXTENSIONS, VIDEO_OUTPUT_EXTENSIONS } from '../../utils/media/media-extensions';

/** The Studio's tab value, re-stated so the page base need not import the Studio's URL module. */
export const STUDIO_TAB_ID = 'studio';

/** The other tab. Not written to the URL — its absence IS this tab (§6.4). */
export const CONVERT_TAB = 'convert';

/**
 * The two tab labels.
 *
 * They live here and not in `views/studio.copy.ts` because the page base must not take an import
 * edge into `components/studio/views/` (that tree reaches `MotiveComponent` and the container —
 * D10). `studio-gate.spec.ts` imports `COPY` and asserts `STUDIO_TAB_LABEL === COPY.titleShort`,
 * so there is still exactly one answer to "what is this thing called"; the assertion is what
 * keeps it that way, not a second constant that nothing compares.
 */
export const CONVERT_TAB_LABEL = 'Quick convert';
export const STUDIO_TAB_LABEL = 'Studio';

/**
 * The image extensions an image→video page accepts — xconfig's decodable IMAGE set (36 entries), read from the
 * tracked copy utils/media/media-formats.generated.json (HANDOFF-L2 #4, 2026-10-11; it was a typed copy of
 * `convert-image-to-video`'s list measured 2026-09-17, which studio-gate.spec still pins it to).
 */
export const STUDIO_IMAGE_EXTENSIONS: ReadonlySet<string> = new Set(IMAGE_EXTENSIONS.map(e => e.toLowerCase()));

/**
 * Every concrete `toType` value — besides the literal `'video'` family bucket — that also means
 * "this page's single output format is a video" (owner ruling 2026-09-18, STUDIO_DESIGN.md §6.1
 * D1 revised again: "ANY non-animated-image → video route", not just mp4/mov/webm).
 *
 * `PageData` (`types/route-types.ts:113`) has no separate `toExtension` field: `toType` doubles
 * as it on every per-format page. This is the FULL set, not a hand-picked few — every distinct
 * `toExtension` xconfig itself stamps `toMediaType: "video"` for, read from
 * `public/feature-metadata/*.json` (see the module KDoc above for the exact derivation and the
 * cross-check against xconfig's own `video.media-formats.json`). 32 entries.
 */
export const VIDEO_CONTAINER_EXTENSIONS: ReadonlySet<string> = new Set(VIDEO_OUTPUT_EXTENSIONS.map(e => e.toLowerCase()));

/**
 * The two image extensions that are themselves animated formats. A page whose ENTIRE input is
 * one of these is an "animated image → video" route, which the owner ruled OUT of the family
 * (2026-09-18) — see the module KDoc's "(2) The animated-only exclusion" for why this is checked
 * against `pageData.fromType`, not `acceptedExtensions`.
 */
export const ANIMATED_ONLY_SOURCE_EXTENSIONS: ReadonlySet<string> = new Set(['gif', 'apng']);

/** The shape of `pageData` this module reads. Structural, so a test needs no PageData. */
export interface StudioPageFacts {
    toType?: string;
    acceptedExtensions?: string[];
    /**
     * The declared source, as a single-element array carrying the RAW extension on every
     * per-extension page (`['gif']`, `['jpg']`, …) or `['image']` on the broad bucket page. Used
     * ONLY for the animated-only-source exclusion below — never for "is this page's source an
     * image", which is what `acceptedExtensions` answers (see the module KDoc's first paragraph
     * for why `fromType` fails at THAT question).
     */
    fromType?: string[];
}

/** The provenance-carrying settings emission, restated structurally for the same reason. */
export interface StudioFlagEmission {
    flags?: { studio?: boolean } | null;
    source?: 'props' | 'api';
}

/**
 * One accept-list entry, as this module compares it.
 *
 * Lower-cased, trimmed, and with a leading dot dropped. The page data measured today carries
 * bare lower-case extensions on all 78 video pages, so none of that normalisation changes a
 * single verdict right now — it is here because the alternative failure is silent: a `'.jpg'`
 * from a regenerated config would drop the tab off a page with no error anywhere.
 */
function normaliseExtension(ext: unknown): string {
    const raw = String(ext == null ? '' : ext).trim().toLowerCase();
    return raw.charAt(0) === '.' ? raw.slice(1) : raw;
}

/**
 * Is this route a still-image→video converter?
 *
 * `toType` answers yes two ways: the family bucket `'video'` (`convert-image-to-video`,
 * `convert-jpg-to-video`, …) OR one of `VIDEO_CONTAINER_EXTENSIONS` (`convert-jpg-to-mp4`,
 * `convert-jpg-to-avi`, `convert-png-to-mkv`, …) — the same page family, just named by its
 * single output extension instead of the bucket. Either way the accept-list check below is what
 * actually separates an image source from a video-input page: `convert-mkv-to-mp4` also has
 * `toType === 'mp4'` but its accept list is `['mkv']`, not an image extension.
 *
 * An EMPTY accept list answers no. "Accepts everything" is what an empty list means to
 * `FileValidationService` (file-upload-manager.ts), and a Studio on a page that takes any
 * file is a Studio that cannot promise anything about its inputs.
 *
 * Last, the animated-only exclusion: a page whose declared source (`fromType`) is exactly
 * `['gif']` or `['apng']` is dark even though gif/apng are themselves in
 * `STUDIO_IMAGE_EXTENSIONS` — see the module KDoc's "(2)" for why this checks `fromType` and not
 * `acceptedExtensions`. `convert-image-to-video` is unaffected: its `fromType` is `['image']`,
 * the bucket, not a single animated extension — it stays lit even though gif/apng are two of its
 * 36 accepted extensions.
 */
export function pageAcceptsStudio(pageData: StudioPageFacts | null | undefined): boolean {
    if (!pageData) return false;
    const { toType } = pageData;
    const isVideoTarget = toType === 'video'
        || (typeof toType === 'string' && VIDEO_CONTAINER_EXTENSIONS.has(toType));
    if (!isVideoTarget) return false;
    const accepted = pageData.acceptedExtensions;
    if (!Array.isArray(accepted) || accepted.length === 0) return false;
    if (!accepted.every((ext) => STUDIO_IMAGE_EXTENSIONS.has(normaliseExtension(ext)))) return false;
    const { fromType } = pageData;
    if (Array.isArray(fromType) && fromType.length === 1
        && ANIMATED_ONLY_SOURCE_EXTENSIONS.has(normaliseExtension(fromType[0]))) return false;
    return true;
}

/**
 * S1-06 (review 7): the format this PAGE is named for, when it is not the Studio's. The Studio always exports MP4
 * (H.264) -- `export/build-request.ts` `STUDIO_TO_EXTENSION`, STUDIO_DESIGN.md §13 keeps other containers out of v2 --
 * while it is lit on all 32 video-container pages (`convert-jpeg-to-mov`, `convert-png-to-webm`, …). The page title
 * and the Quick convert tab on the same page promise that container, so the Studio must say it makes MP4. Answers
 * the upper-case extension ('MOV', 'WEBM'), or null on an MP4 page, the `'video'` bucket or anything unknown.
 */
export function studioPageFormatOtherThanMp4(pageData: { toType?: unknown } | null | undefined): string | null {
    const to = normaliseExtension(pageData?.toType);
    if (!to || to === 'mp4' || !VIDEO_CONTAINER_EXTENSIONS.has(to)) return null;
    return to.toUpperCase();
}

/**
 * Does the fleet say the Studio is off? Only an API-sourced explicit `false` does (§6.1).
 *
 * `undefined` is not `false`: an edge on a settings document written before the flag existed,
 * and every props-sourced emission, both mean "we have not been told otherwise". The backend
 * flag defaults TRUE (§11.0 R9), which is the opposite default from every other flag on this
 * page, and it is why this reads as "hide on an explicit false" rather than "show on a true".
 */
export function studioFlagHides(emission: StudioFlagEmission | null | undefined): boolean {
    return emission?.source === 'api' && emission?.flags?.studio === false;
}

/** Both halves. The tab is visible unless one of them says no. */
export function studioTabVisible(
    pageData: StudioPageFacts | null | undefined,
    emission: StudioFlagEmission | null | undefined,
): boolean {
    return pageAcceptsStudio(pageData) && !studioFlagHides(emission);
}
